import {
	useComponentInterceptor,
	useComponentOverride,
	useElementVisibility,
	usePageContextOptional,
} from "@judo/core";
import type {
	Button as ButtonElement,
	ButtonGroup as ButtonGroupElement,
	Flex,
	Formatted,
	Input,
	Link,
	TabController,
	Table,
	VisualElement,
} from "@judo/model-api";
import { InputRenderer } from "../inputs/InputRenderer";
import { DividerRenderer } from "./DividerRenderer";
import { FabButtonGroupRenderer } from "./FabButtonGroupRenderer";
import { FlexRenderer } from "./FlexRenderer";
import { FormattedRenderer } from "./FormattedRenderer";
import { InlineButtonGroupRenderer } from "./InlineButtonGroupRenderer";
import { LinkRenderer } from "./LinkRenderer";
import { SpacerRenderer } from "./SpacerRenderer";
import { StandaloneButtonRenderer } from "./StandaloneButtonRenderer";
import { SubThemeWrapper } from "./SubThemeWrapper";
import { TabControllerRenderer } from "./TabControllerRenderer";
import { TableRenderer } from "./TableRenderer";

export interface VisualElementRendererProps {
	element: VisualElement;
	readOnly?: boolean;
}

/**
 * Dispatcher for all visual element types.
 * Checks for overrides in priority order:
 * 1. sourceId-based override (CustomizationsConfig.components)
 * 2. type-based interceptor (CustomizationsConfig.componentInterceptors)
 * 3. Default rendering by @type
 *
 * When the element has a `subTheme` set and a matching provider is registered,
 * the rendered output is wrapped in a scoped MUI ThemeProvider via SubThemeWrapper.
 */
export function VisualElementRenderer({ element, readOnly }: VisualElementRendererProps) {
	// Check element visibility (hiddenBy dynamic data, static hidden, customization overrides)
	const hidden = useElementVisibility(element);
	if (hidden) {
		return null;
	}

	// 1. Check for sourceId-based custom component override (highest priority)
	const CustomComponent = useComponentOverride(element);
	const pageContext = usePageContextOptional();
	if (CustomComponent) {
		const page = pageContext?.pageDefinition;
		if (!page) return null;
		return (
			<SubThemeWrapper element={element}>
				<CustomComponent element={element} page={page} />
			</SubThemeWrapper>
		);
	}

	// 2. Check for type-based interceptor
	const InterceptedComponent = useComponentInterceptor(element);
	if (InterceptedComponent) {
		const page = pageContext?.pageDefinition;
		if (!page) return null;
		return (
			<SubThemeWrapper element={element}>
				<InterceptedComponent element={element} page={page} />
			</SubThemeWrapper>
		);
	}

	// 3. Default rendering by @type
	const content = renderByType(element, readOnly);
	if (!content) return null;

	return <SubThemeWrapper element={element}>{content}</SubThemeWrapper>;
}

/**
 * Default rendering dispatch by element @type.
 */
function renderByType(element: VisualElement, readOnly?: boolean): React.ReactNode {
	switch (element["@type"]) {
		case "Flex":
		case "PageContainer":
			return <FlexRenderer element={element as Flex} readOnly={readOnly} />;

		case "Table":
			return <TableRenderer element={element as Table} />;

		case "Link":
			return <LinkRenderer element={element as Link} readOnly={readOnly} />;

		case "ButtonGroup": {
			const bg = element as ButtonGroupElement;
			if (bg.isFab) {
				return <FabButtonGroupRenderer element={bg} />;
			}
			return <InlineButtonGroupRenderer element={bg} />;
		}

		case "Button":
			// Standalone buttons (e.g., relation navigation buttons like "View Matter Table")
			return <StandaloneButtonRenderer element={element as ButtonElement} />;

		case "TabController":
			return <TabControllerRenderer element={element as TabController} readOnly={readOnly} />;

		// Input types
		case "TextInput":
		case "NumericInput":
		case "DateInput":
		case "DateTimeInput":
		case "TimeInput":
		case "EnumerationCombo":
		case "EnumerationRadio":
		case "EnumerationToggleButtonbar":
		case "Checkbox":
		case "Switch":
		case "TrinaryLogicCombo":
		case "TextArea":
		case "BinaryTypeInput":
		case "PasswordInput":
			return <InputRenderer element={element as Input} readOnly={readOnly} />;

		case "Spacer":
			return <SpacerRenderer element={element} />;

		case "Divider":
			return <DividerRenderer element={element} />;

		case "Formatted":
			return <FormattedRenderer element={element as Formatted} />;

		default:
			console.warn(`Unknown element type: ${element["@type"]}`);
			return null;
	}
}
